import React, { useEffect, useMemo, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { toast } from "sonner";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";
import { Loader2, Plus, Trash2, Save, BookOpen, Database, ClipboardList } from "lucide-react";
const COURSE_GROUPS = [
  { label: "Trichology", codes: [
    ["Certified Scalp Scientist", ["TRI-101","TRI-102","TRI-103","TRI-104","TRI-105","TRI-106","TRI-107","TRI-108","TRI-109","TRI-110","TRI-111","TRI-112"]],
    ["Associate Trichologist",  ["TRI-201","TRI-202","TRI-203","TRI-204","TRI-205","TRI-206","TRI-207","TRI-208","TRI-209","TRI-210","TRI-211","TRI-212"]],
    ["Holistic Trichology Practitioner", ["TRI-301","TRI-302","TRI-303","TRI-304","TRI-305","TRI-306","TRI-307","TRI-308","TRI-309","TRI-310","TRI-311","TRI-312"]],
    ["Clinical Residency", ["CTR-401","CTR-402","CTR-403","CTR-404","CTR-405","CTR-406","CTR-407","CTR-408"]],
    ["Doctor of Trichology Science", ["DTS-701","DTS-702","DTS-703","DTS-704","DTS-705","DTS-706","DTS-707","DTS-708","DTS-709","DTS-710","DTS-799"]],
  ]},
  { label: "Herbal Medicine Practitioner", codes: [["HMP", ["HMP-101","HMP-102","HMP-103","HMP-104","HMP-105","HMP-106","HMP-107","HMP-108","HMP-109","HMP-110","HMP-111","HMP-112"]]] },
  { label: "Functional Health Practitioner", codes: [["FHP", ["FHP-101","FHP-102","FHP-103","FHP-104","FHP-105","FHP-106","FHP-107","FHP-108","FHP-109","FHP-110","FHP-111","FHP-112","FHP-113","FHP-114","FHP-115","FHP-116","FHP-117","FHP-118"]]] },
  { label: "Holistic Wellness Coach", codes: [["HWC", ["HWC-101","HWC-102","HWC-103","HWC-104","HWC-105","HWC-106","HWC-107","HWC-108","HWC-109","HWC-110","HWC-111","HWC-112"]]] },
];

export default function AdminUniversityLMS() {
  const [code, setCode] = useState("TRI-101");
  const [data, setData] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("modules"); // modules | assignments

  const load = async (c) => {
    setLoading(true);
    try {
      const r = await universityApi.lmsAdminModules(c);
      setData(r.data);
      try {
        const ar = await universityApi.assignmentsAdminList(c);
        setAssignments(ar.data?.items || []);
      } catch { setAssignments([]); }
    } catch (e) {
      toast.error(formatApiError(e, "Could not load"));
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(code); }, [code]);

  const seedAll = async () => {
    try {
      const r = await universityApi.lmsAdminSeedDefaults();
      toast.success(`Seeded ${r.data.modules_created} modules + ${r.data.lessons_created} lessons`);
      await load(code);
    } catch (e) {
      toast.error(formatApiError(e, "Seed failed"));
    }
  };

  return (
    <AdminShell door="university" title="LMS Builder">
      <div data-testid="admin-lms-page">
        {/* Course picker */}
        <aside className="lg:col-span-1">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 sticky top-20 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wide text-slate-500">Courses</div>
              <button onClick={seedAll} className="text-[11px] text-purple-900 hover:underline inline-flex items-center gap-1" data-testid="admin-lms-seed-btn">
                <Database className="w-3 h-3" />Seed defaults
              </button>
            </div>
            {COURSE_GROUPS.map((g) => (
              <div key={g.label} className="mb-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">{g.label}</div>
                {g.codes.map(([sub, codes]) => (
                  <div key={sub} className="mb-2">
                    <div className="text-[11px] text-slate-500">{sub}</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {codes.map((c) => (
                        <button
                          key={c}
                          onClick={() => setCode(c)}
                          className={`text-[11px] font-mono px-2 py-1 rounded-lg border ${code === c ? "border-purple-900 bg-purple-50 text-purple-900" : "border-slate-200 text-slate-600 hover:border-purple-300"}`}
                          data-testid={`admin-lms-course-${c}`}
                        >{c}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </aside>

        {/* Content editor */}
        <section className="lg:col-span-3">
          <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-slate-500 mb-1">
            <BookOpen className="w-3.5 h-3.5" />Editing course <span className="font-mono text-purple-900">{code}</span>
          </div>
          <div className="flex items-center gap-2 mb-4">
            <button onClick={() => setTab("modules")} className={`text-xs px-3 py-1.5 rounded-full ${tab === "modules" ? "bg-purple-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="admin-lms-tab-modules">Modules & Lessons</button>
            <button onClick={() => setTab("assignments")} className={`text-xs px-3 py-1.5 rounded-full ${tab === "assignments" ? "bg-purple-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="admin-lms-tab-assignments">Assignments</button>
          </div>
          {loading ? (
            <div className="text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
          ) : !data ? (
            <div className="text-slate-500">Select a course.</div>
          ) : tab === "modules" ? (
            <CourseEditor code={code} data={data} reload={() => load(code)} />
          ) : (
            <AssignmentsEditor code={code} items={assignments} reload={() => load(code)} />
          )}
        </section>
      </div>
    </AdminShell>
  );
}

function CourseEditor({ code, data, reload }) {
  const [newModule, setNewModule] = useState({ title: "", order: 0, description: "" });

  const addModule = async () => {
    if (!newModule.title.trim()) return toast.error("Module title required");
    try {
      await universityApi.lmsAdminCreateModule({ ...newModule, course_code: code });
      toast.success("Module created");
      setNewModule({ title: "", order: 0, description: "" });
      reload();
    } catch (e) { toast.error(formatApiError(e, "Create failed")); }
  };

  const delModule = async (id) => {
    if (!confirm("Delete module and all its lessons?")) return;
    try {
      await universityApi.lmsAdminDeleteModule(id);
      toast.success("Module deleted");
      reload();
    } catch (e) { toast.error(formatApiError(e, "Delete failed")); }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Add module</div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <input className="md:col-span-6 rounded-xl border border-slate-200 px-3 h-9 text-sm" placeholder="Title" value={newModule.title} onChange={(e) => setNewModule((s) => ({ ...s, title: e.target.value }))} data-testid="admin-lms-new-module-title" />
          <input className="md:col-span-2 rounded-xl border border-slate-200 px-3 h-9 text-sm" type="number" placeholder="Order" value={newModule.order} onChange={(e) => setNewModule((s) => ({ ...s, order: Number(e.target.value) }))} />
          <input className="md:col-span-3 rounded-xl border border-slate-200 px-3 h-9 text-sm" placeholder="Description (optional)" value={newModule.description} onChange={(e) => setNewModule((s) => ({ ...s, description: e.target.value }))} />
          <button onClick={addModule} className="md:col-span-1 rounded-xl bg-purple-900 hover:bg-purple-800 text-white h-9 text-sm inline-flex items-center justify-center" data-testid="admin-lms-add-module-btn"><Plus className="w-4 h-4" /></button>
        </div>
      </div>

      {(data.modules || []).map((m) => (
        <ModuleBlock key={m.id} module={m} courseCode={code} reload={reload} onDelete={() => delModule(m.id)} />
      ))}
      {(data.modules || []).length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">No modules yet. Click "Seed defaults" or add one above.</div>
      )}
    </div>
  );
}

function ModuleBlock({ module: m, courseCode, reload, onDelete }) {
  const [title, setTitle] = useState(m.title);
  const [open, setOpen] = useState(false);
  const [newLesson, setNewLesson] = useState({ title: "", order: (m.lessons || []).length + 1, content_md: "", video_url: "", duration_minutes: 0, required: true });

  const saveTitle = async () => {
    try { await universityApi.lmsAdminPatchModule(m.id, { title }); toast.success("Saved"); reload(); }
    catch (e) { toast.error(formatApiError(e, "Save failed")); }
  };
  const addLesson = async () => {
    if (!newLesson.title.trim()) return toast.error("Lesson title required");
    try {
      await universityApi.lmsAdminCreateLesson({ ...newLesson, module_id: m.id, course_code: courseCode });
      toast.success("Lesson created");
      setNewLesson({ title: "", order: (m.lessons || []).length + 2, content_md: "", video_url: "", duration_minutes: 0, required: true });
      reload();
    } catch (e) { toast.error(formatApiError(e, "Create failed")); }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`admin-lms-module-${m.id}`}>
      <div className="flex items-center gap-3">
        <input className="flex-1 rounded-xl border border-slate-200 px-3 h-9 text-sm" value={title} onChange={(e) => setTitle(e.target.value)} />
        <button onClick={saveTitle} className="rounded-xl border border-purple-300 text-purple-900 h-9 px-3 text-xs inline-flex items-center gap-1"><Save className="w-3 h-3" />Save</button>
        <button onClick={() => setOpen(!open)} className="rounded-xl border border-slate-200 h-9 px-3 text-xs">{open ? "Hide lessons" : `Lessons (${(m.lessons || []).length})`}</button>
        <button onClick={onDelete} className="rounded-xl border border-rose-200 text-rose-700 h-9 px-3 text-xs inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /></button>
      </div>
      {open && (
        <div className="mt-3 space-y-2">
          {(m.lessons || []).map((l) => (
            <LessonRow key={l.id} lesson={l} reload={reload} />
          ))}
          <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 mt-2">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Add lesson</div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-2">
              <input className="md:col-span-7 rounded-xl border border-slate-200 px-3 h-9 text-sm" placeholder="Lesson title" value={newLesson.title} onChange={(e) => setNewLesson((s) => ({ ...s, title: e.target.value }))} data-testid={`admin-lms-new-lesson-title-${m.id}`} />
              <input className="md:col-span-2 rounded-xl border border-slate-200 px-3 h-9 text-sm" type="number" placeholder="Order" value={newLesson.order} onChange={(e) => setNewLesson((s) => ({ ...s, order: Number(e.target.value) }))} />
              <input className="md:col-span-2 rounded-xl border border-slate-200 px-3 h-9 text-sm" type="number" placeholder="Duration min" value={newLesson.duration_minutes} onChange={(e) => setNewLesson((s) => ({ ...s, duration_minutes: Number(e.target.value) }))} />
              <button onClick={addLesson} className="md:col-span-1 rounded-xl bg-purple-900 text-white h-9 text-sm inline-flex items-center justify-center" data-testid={`admin-lms-add-lesson-btn-${m.id}`}><Plus className="w-4 h-4" /></button>
            </div>
            <textarea
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-mono"
              rows={4}
              placeholder="Markdown content (supports [vimeo:ID] and [loom:ID] shortcodes)"
              value={newLesson.content_md}
              onChange={(e) => setNewLesson((s) => ({ ...s, content_md: e.target.value }))}
            />
          </div>
        </div>
      )}
    </section>
  );
}

function LessonRow({ lesson, reload }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState({
    title: lesson.title || "",
    content_md: lesson.content_md || "",
    video_url: lesson.video_url || "",
    duration_minutes: lesson.duration_minutes || 0,
    required: !!lesson.required,
  });
  const save = async () => {
    try { await universityApi.lmsAdminPatchLesson(lesson.id, body); toast.success("Saved"); reload(); }
    catch (e) { toast.error(formatApiError(e, "Save failed")); }
  };
  const del = async () => {
    if (!confirm("Delete lesson?")) return;
    try { await universityApi.lmsAdminDeleteLesson(lesson.id); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(formatApiError(e, "Delete failed")); }
  };

  return (
    <div className="rounded-xl border border-slate-100 bg-white p-3" data-testid={`admin-lms-lesson-${lesson.id}`}>
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-slate-400 font-mono w-6">#{lesson.order}</span>
        <input className="flex-1 rounded-xl border border-slate-200 px-3 h-9 text-sm" value={body.title} onChange={(e) => setBody((s) => ({ ...s, title: e.target.value }))} />
        <button onClick={() => setOpen(!open)} className="text-xs text-slate-600 hover:underline">{open ? "Hide" : "Edit content"}</button>
        <button onClick={save} className="rounded-xl border border-purple-300 text-purple-900 h-9 px-3 text-xs">Save</button>
        <button onClick={del} className="rounded-xl border border-rose-200 text-rose-700 h-9 px-3 text-xs"><Trash2 className="w-3 h-3" /></button>
      </div>
      {open && (
        <div className="mt-2 space-y-2">
          <textarea
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-mono"
            rows={8}
            placeholder="Markdown — use [vimeo:ID] or [loom:ID] for video embeds"
            value={body.content_md}
            onChange={(e) => setBody((s) => ({ ...s, content_md: e.target.value }))}
          />
          <div className="grid grid-cols-12 gap-2">
            <input className="col-span-7 rounded-xl border border-slate-200 px-3 h-9 text-sm" placeholder="Video URL (optional)" value={body.video_url} onChange={(e) => setBody((s) => ({ ...s, video_url: e.target.value }))} />
            <input className="col-span-3 rounded-xl border border-slate-200 px-3 h-9 text-sm" type="number" placeholder="Duration min" value={body.duration_minutes} onChange={(e) => setBody((s) => ({ ...s, duration_minutes: Number(e.target.value) }))} />
            <label className="col-span-2 inline-flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={body.required} onChange={(e) => setBody((s) => ({ ...s, required: e.target.checked }))} />Required
            </label>
          </div>
        </div>
      )}
    </div>
  );
}


function AssignmentsEditor({ code, items, reload }) {
  const [editing, setEditing] = useState(null); // assignment object or null
  const blank = useMemo(() => ({
    course_code: code,
    title: "",
    description: "",
    instructions: "",
    due_at: "",
    passing_score: 80,
    word_count_min: 0,
    required: true,
    required_keywords: [],
    rubric_criteria: [
      { name: "Completeness", description: "All required sections present", points: 20 },
      { name: "Accuracy", description: "Factual correctness", points: 20 },
      { name: "Application of Knowledge", description: "Demonstrates understanding", points: 20 },
      { name: "Professionalism", description: "Tone, clarity, formatting", points: 20 },
      { name: "Critical Thinking", description: "Analytical depth", points: 20 },
    ],
  }), [code]);

  const startNew = () => setEditing(blank);
  const startEdit = (a) => setEditing({ ...a, required_keywords: a.required_keywords || [], rubric_criteria: a.rubric_criteria || [] });

  const remove = async (id) => {
    if (!confirm("Delete assignment? This will not delete existing submissions.")) return;
    try {
      await universityApi.assignmentAdminDelete(id);
      toast.success("Assignment deleted");
      reload();
    } catch (e) { toast.error(formatApiError(e, "Delete failed")); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide text-slate-500 inline-flex items-center gap-1.5">
          <ClipboardList className="w-3.5 h-3.5" />Assignments for <span className="font-mono text-purple-900">{code}</span>
        </div>
        <button onClick={startNew} className="rounded-xl bg-purple-900 text-white h-9 px-3 text-sm inline-flex items-center gap-1.5" data-testid="admin-assign-new">
          <Plus className="w-4 h-4" />New assignment
        </button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          No assignments yet for this course. Click &ldquo;New assignment&rdquo; to add one.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((a) => (
            <li key={a.id} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center justify-between gap-3" data-testid={`admin-assign-row-${a.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{a.title}</div>
                <div className="text-[11px] text-slate-500">Passing {a.passing_score}% · {a.required ? "Required" : "Optional"} · {(a.rubric_criteria || []).length} criteria</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => startEdit(a)} className="text-xs text-purple-900 hover:underline">Edit</button>
                <button onClick={() => remove(a.id)} className="text-xs text-rose-600 hover:underline inline-flex items-center gap-1"><Trash2 className="w-3 h-3" />Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <AssignmentForm
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

function AssignmentForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setRubric = (i, k, v) => setForm((s) => ({
    ...s, rubric_criteria: s.rubric_criteria.map((r, idx) => idx === i ? { ...r, [k]: v } : r),
  }));
  const addRubric = () => setForm((s) => ({ ...s, rubric_criteria: [...s.rubric_criteria, { name: "", description: "", points: 0 }] }));
  const delRubric = (i) => setForm((s) => ({ ...s, rubric_criteria: s.rubric_criteria.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    setSaving(true);
    try {
      const body = {
        ...form,
        passing_score: Number(form.passing_score) || 80,
        word_count_min: Number(form.word_count_min) || 0,
        required_keywords: form.required_keywords || [],
        rubric_criteria: form.rubric_criteria.map((r) => ({
          name: r.name, description: r.description || "", points: Number(r.points) || 0,
        })),
      };
      if (form.id) {
        await universityApi.assignmentAdminPatch(form.id, body);
        toast.success("Assignment updated");
      } else {
        await universityApi.assignmentAdminCreate(body);
        toast.success("Assignment created");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e, "Save failed"));
    } finally { setSaving(false); }
  };

  const max = form.rubric_criteria.reduce((s, r) => s + (Number(r.points) || 0), 0);

  return (
    <div className="rounded-2xl border border-purple-200 bg-purple-50/40 p-5" data-testid="admin-assign-form">
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-3">{form.id ? "Edit assignment" : "New assignment"}</div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
        <input className="md:col-span-8 rounded-xl border border-slate-200 px-3 h-9 text-sm" placeholder="Title" value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="admin-assign-title" />
        <input className="md:col-span-2 rounded-xl border border-slate-200 px-3 h-9 text-sm" type="number" placeholder="Passing %" value={form.passing_score} onChange={(e) => set("passing_score", e.target.value)} />
        <input className="md:col-span-2 rounded-xl border border-slate-200 px-3 h-9 text-sm" type="number" placeholder="Min words" value={form.word_count_min} onChange={(e) => set("word_count_min", e.target.value)} />
      </div>
      <input className="mt-2 w-full rounded-xl border border-slate-200 px-3 h-9 text-sm" placeholder="Short description (shown on course page)" value={form.description} onChange={(e) => set("description", e.target.value)} />
      <textarea className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" rows={4} placeholder="Instructions (markdown supported)" value={form.instructions} onChange={(e) => set("instructions", e.target.value)} />
      <div className="mt-3 flex items-center gap-3 text-xs text-slate-600">
        <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={!!form.required} onChange={(e) => set("required", e.target.checked)} />Required (counts toward course completion)</label>
        <input className="flex-1 rounded-xl border border-slate-200 px-3 h-9 text-sm" placeholder="Due date (YYYY-MM-DD, optional)" value={form.due_at || ""} onChange={(e) => set("due_at", e.target.value)} />
      </div>

      {/* Rubric editor */}
      <div className="mt-4 rounded-xl bg-white border border-slate-200 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wide text-slate-500">Rubric · total {max} pts</div>
          <button onClick={addRubric} className="text-[11px] text-purple-900 hover:underline inline-flex items-center gap-1"><Plus className="w-3 h-3" />Add criterion</button>
        </div>
        <div className="space-y-2">
          {form.rubric_criteria.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2" data-testid={`admin-assign-rubric-${i}`}>
              <input className="col-span-4 rounded-xl border border-slate-200 px-3 h-9 text-sm" placeholder="Criterion name" value={r.name} onChange={(e) => setRubric(i, "name", e.target.value)} />
              <input className="col-span-6 rounded-xl border border-slate-200 px-3 h-9 text-sm" placeholder="Description (shown to student & grader)" value={r.description} onChange={(e) => setRubric(i, "description", e.target.value)} />
              <input className="col-span-1 rounded-xl border border-slate-200 px-3 h-9 text-sm font-mono" type="number" placeholder="pts" value={r.points} onChange={(e) => setRubric(i, "points", e.target.value)} />
              <button onClick={() => delRubric(i)} className="col-span-1 text-rose-600 inline-flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        {max !== 100 && (
          <div className="text-[11px] text-amber-700 mt-2">Rubric total is {max} (most assignments use 100; grade %  = earned / total).</div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <button onClick={onCancel} className="text-xs text-slate-500 hover:underline">Cancel</button>
        <button onClick={save} disabled={saving} className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white h-9 px-4 text-sm inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="admin-assign-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {form.id ? "Save changes" : "Create assignment"}
        </button>
      </div>
    </div>
  );
}

